import { Link, useLocation } from 'react-router-dom';

import logo from '../images/metered-mining-logo.svg';
import logoDark from '../images/metered-mining-logo-dark.svg';

import { useSession } from '../hooks/useSession';
import ThemeToggle from './ThemeToggle';

/**
 * Application shell header.
 *
 * Establishes that the customer is inside a real Metered Mining product, and that this
 * product has sections. It is not marketing-site navigation.
 *
 * ---------------------------------------------------------------------------
 * THE WORDMARK IS THE SUPPLIED ASSET.
 *
 * `images/metered-mining-logo.svg` exactly as the owner provided it: not redrawn, not
 * rasterised, not recoloured, not cropped. It is a wide lockup (2048x207, roughly 10:1),
 * so it is sized by HEIGHT with `w-auto` -- constraining the width instead would either
 * distort it or make the header tall enough to push the page down.
 *
 * The <img> carries the product name as its alt text, so the link keeps the same
 * accessible name it had when the wordmark was type.
 *
 * ---------------------------------------------------------------------------
 * TWO VARIANTS, BECAUSE THE WORDMARK IS INK AND THE HEADER IS NOT ALWAYS PAPER.
 *
 * The supplied lockup letters in near-black, which disappears against the dark theme's
 * header (`--mm-surface`, #1b1d21). `-dark.svg` is the SAME asset with the five wordmark
 * fills inverted channel-wise -- those five shades are anti-aliasing steps that blend
 * the lettering toward a white page, so inverting them blends it toward a dark one by
 * the same amounts. Every rect coordinate, the viewBox and the embedded icon PNG are
 * byte-identical; the icon is deliberately untouched in both.
 *
 * BOTH ARE RENDERED AND CSS CHOOSES. The theme is a `data-theme` attribute on <html>
 * set before first paint, not a media query, so a <picture> with
 * `prefers-color-scheme` would ignore the toggle entirely. Hiding one with CSS instead
 * means the swap happens in the same repaint as every other token -- no React
 * re-render, no second network fetch mid-toggle, and no flash of black-on-black.
 *
 * `display: none` also removes the hidden one from the accessibility tree, so exactly
 * one wordmark is ever announced even though both carry the same alt text. Rules live
 * in styles/index.css next to the theme tokens they follow.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS REAL AND WHAT IS NOT
 *
 * Configure and Orders are real destinations and are links. Orders became real in M5:
 * a signed-in customer needs somewhere to find an order without the reference, and the
 * account API already scopes every query to them.
 *
 * MY ACCOUNT IS NOW REAL, and became real for a reason rather than because the label
 * existed. The identity model is still "proved control of an email address" -- there is
 * no profile, no password and no preferences -- but the DEFAULT PAYOUT WALLET is
 * genuine account state a customer owns and must be able to change. That is the whole
 * page; it is not a settings project.
 *
 * The `pending` affordance below is kept: it costs nothing and is the right treatment
 * for the next nav item that is laid out before it is built.
 */

interface NavItem {
  label: string;
  to?: string;
  /** Present in the layout, not yet a destination. */
  pending?: boolean;
}

const SECTIONS: NavItem[] = [
  { label: 'Configure', to: '/' },
  { label: 'Orders', to: '/orders' },
  { label: 'My Account', to: '/account' },
];

export default function SiteHeader() {
  const location = useLocation();
  const { state, signOut } = useSession();

  function isCurrent(item: NavItem): boolean {
    if (!item.to) return false;
    if (item.to === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.to);
  }

  return (
    <header
      className="border-b"
      style={{ borderColor: 'var(--mm-border)', background: 'var(--mm-surface)' }}
    >
      <div className="mx-auto flex w-full max-w-[1240px] flex-col items-start gap-2 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-8 sm:py-4 lg:px-10">
        <Link to="/" className="flex shrink-0 items-center" aria-label="Metered Mining">
          {/*
            Height-constrained, width automatic. A 10:1 lockup pinned by width would
            either distort or set the header height; this keeps the existing header
            geometry and lets the mark find its own width.

            The two variants are sized identically, so the header geometry does not move
            when the theme changes.
          */}
          <img
            src={logo}
            alt="Metered Mining"
            data-testid="site-logo"
            className="mm-logo mm-logo--light h-6 w-auto sm:h-7"
          />
          <img
            src={logoDark}
            alt="Metered Mining"
            data-testid="site-logo-dark"
            className="mm-logo mm-logo--dark h-6 w-auto sm:h-7"
          />
        </Link>

        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <nav aria-label="Sections">
            <ul className="m-0 flex list-none items-center gap-4 p-0 sm:gap-7">
              {SECTIONS.map((section) => {
                const current = isCurrent(section);
                const testId = `nav-${section.label.toLowerCase().replace(/\s+/g, '-')}`;

                return (
                  <li key={section.label}>
                    {section.to ? (
                      <Link
                        to={section.to}
                        aria-current={current ? 'page' : undefined}
                        data-testid={testId}
                        className="relative py-1 text-sm"
                        style={{
                          color: current ? 'var(--mm-text)' : 'var(--mm-text-muted)',
                          fontWeight: current ? 600 : 500,
                        }}
                      >
                        {section.label}
                        {current && (
                          <span
                            aria-hidden="true"
                            className="absolute -bottom-[3px] left-0 right-0 h-[2px] rounded-full"
                            style={{ background: 'var(--mm-accent)' }}
                          />
                        )}
                      </Link>
                    ) : (
                      <span
                        data-testid={testId}
                        className="py-1 text-sm font-medium"
                        style={{ color: 'var(--mm-text-subtle)' }}
                      >
                        {section.label}
                        <span className="sr-only"> (coming soon)</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <ThemeToggle />

          {state.status === 'authenticated' ? (
            <button
              type="button"
              onClick={() => void signOut()}
              data-testid="sign-out"
              className="text-sm font-medium"
              style={{ color: 'var(--mm-text-muted)' }}
            >
              Sign out
            </button>
          ) : state.status === 'anonymous' ? (
            <Link
              to="/sign-in"
              data-testid="sign-in-link"
              className="text-sm font-medium"
              style={{ color: 'var(--mm-accent)' }}
            >
              Sign in
            </Link>
          ) : null}
        </div>
      </div>
    </header>
  );
}
